import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';

export interface SiteSettings {
  logo_url: string;
  hero_image: string;
  hero_title: string;
  hero_subtitle: string;
  hero_description: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  footer_description: string;
  facebook_url: string;
  linkedin_url: string;
  twitter_url: string;
  youtube_url: string;
}

const defaultSettings: SiteSettings = {
  logo_url: 'https://static.readdy.ai/image/2be2e13d473462539e3856fa80adc749/b8a5bf7368b3782df4f04955c4dc8e00.png',
  hero_image: 'https://readdy.ai/api/search-image?query=Stunning%20aerial%20view%20of%20a%20busy%20international%20cargo%20port%20at%20golden%20hour%20with%20shipping%20containers%20cranes%20and%20cargo%20ships%20warm%20amber%20tones%20cinematic%20atmosphere%20vast%20global%20trade%20scene%20professional%20photography%20ultra%20wide%20angle&width=1600&height=900&seq=hero-main&orientation=landscape',
  hero_title: 'Global Trade,',
  hero_subtitle: 'Delivered.',
  hero_description: 'BEKAALI ENTERPRISES LIMITED connects East African businesses with premium Chinese manufacturing — delivering exceptional value through trade, procurement, and logistics solutions across continents.',
  contact_email: 'info@bekaali.com',
  contact_phone: '+254 700 000 000',
  contact_address: 'Nairobi, Kenya',
  footer_description: 'BEKAALI ENTERPRISES LIMITED is your trusted partner for trade, procurement, and logistics — connecting East African businesses with premium Chinese manufacturing and global supply chains.',
  facebook_url: '#',
  linkedin_url: '#',
  twitter_url: '#',
  youtube_url: '#',
};

interface SiteSettingsContextValue {
  settings: SiteSettings;
  loading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
  updateSettings: (updates: Partial<SiteSettings>) => Promise<{ error: string | null }>;
}

const SiteSettingsContext = createContext<SiteSettingsContextValue | null>(null);

export function SiteSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('site_settings')
        .select('*')
        .eq('id', 1)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (data) {
        const merged: SiteSettings = { ...defaultSettings };
        for (const key of Object.keys(defaultSettings) as (keyof SiteSettings)[]) {
          if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
            merged[key] = data[key] as string;
          }
        }
        setSettings(merged);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to load site settings:', err);
      setError('Could not load site settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSettings = useCallback(async (updates: Partial<SiteSettings>) => {
    try {
      const { error: updateError } = await supabase
        .from('site_settings')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', 1);

      if (updateError) throw updateError;

      setSettings((prev) => ({ ...prev, ...updates }));
      return { error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save settings';
      console.error('Failed to update site settings:', err);
      return { error: message };
    }
  }, []);

  return (
    <SiteSettingsContext.Provider value={{ settings, loading, error, refreshSettings: fetchSettings, updateSettings }}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  const ctx = useContext(SiteSettingsContext);
  if (!ctx) throw new Error('useSiteSettings must be inside SiteSettingsProvider');
  return ctx;
}